import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MessageCircle, X, Send, Loader2, Bot, User, Minimize2, Trash2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { assistantAnalytics } from '@/services/assistantAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// CENTRALIZED ASSISTANT CONTEXT CONFIGURATION
// ============================================

const ASSISTANT_CONTEXT_CONFIG = {
  // Home Page
  '/': {
    domain: null,
    title: 'Zodiac Intelligence Assistant',
    welcome: "Hello! I'm the Zodiac Intelligence Assistant. How may I support you today? I can help with programs, research, workforce intelligence, or learning.",
    placeholder: 'Ask me anything about Zodiac Institute...',
    quickActions: [
      { label: 'Explore Programs', prompt: 'Help me understand the programs available at Zodiac Workforce Intelligence Institute. What categories do you offer?' },
      { label: 'Learn About ProLab', prompt: 'What is Zodiac ProLab and how can it help me develop AI workforce capabilities?' },
      { label: 'Ask About Admissions', prompt: 'I want to learn about admissions. What are the general requirements and application process?' },
      { label: 'Discover JusWork', prompt: 'What is JusWork and how does it relate to workforce intelligence?' },
      { label: 'What makes Zodiac different?', prompt: 'What makes the Zodiac Workforce Intelligence Institute unique compared to other educational institutions?' }
    ]
  },
  
  // Programs Section
  '/programs': {
    domain: 'admissions',
    title: 'Admissions Assistant',
    welcome: "Hello! I'm your Zodiac Admissions Assistant. I can help you explore programs, understand requirements, and find the right fit for your career goals.",
    placeholder: 'Ask about programs, tuition, or requirements...',
    quickActions: [
      { label: 'Compare Programs', prompt: 'Help me compare the different program categories: Executive Professional Masters, Training Certificates, Courses, and Licensing. What are the key differences?' },
      { label: 'Show Tuition Information', prompt: 'What is the tuition range for your programs? Are there different pricing tiers based on program type?' },
      { label: 'Which program fits me?', prompt: 'I want to find the right program for my career goals. Can you help me understand which program type might be best based on my professional background?' },
      { label: 'Explain Executive Masters', prompt: 'Tell me about the Executive Professional Masters programs. What specializations are available and what makes them suitable for senior professionals?' },
      { label: 'Training Certificates', prompt: 'What Training Certificate programs do you offer? How long do they take and what credentials do they provide?' }
    ]
  },
  
  // Admissions Page
  '/admissions': {
    domain: 'admissions',
    title: 'Admissions Assistant',
    welcome: "Welcome! I'm here to guide you through our enrollment process. Ask me about programs, eligibility, or next steps.",
    placeholder: 'Ask about enrollment or application process...',
    quickActions: [
      { label: 'How do I apply?', prompt: 'Walk me through the application process step by step. What documents do I need and how long does it take?' },
      { label: 'Which program fits my goals?', prompt: 'I want to advance my career in workforce intelligence. Can you help me identify which program aligns with my professional goals?' },
      { label: 'Show tuition and duration', prompt: 'What are the tuition costs and program durations across different program types? Is there financial aid available?' },
      { label: 'What are the admission steps?', prompt: 'What are the key admission steps from initial inquiry to enrollment? What should I prepare?' },
      { label: 'Help me choose a program', prompt: 'I need guidance choosing between programs. What factors should I consider when selecting the right fit?' }
    ]
  },
  
  // Research Section
  '/research': {
    domain: 'research',
    title: 'Research Assistant',
    welcome: "Hello! I'm your Zodiac Research Assistant. I can help explain frameworks, methodologies, and research publications.",
    placeholder: 'Ask about research, frameworks, or methodology...',
    quickActions: [
      { label: 'Explain Workforce Intelligence', prompt: 'What is workforce intelligence as a field of study? How does Zodiac approach workforce intelligence research?' },
      { label: 'Show research themes', prompt: 'What are the main research themes and focus areas at the Zodiac Workforce Intelligence Institute?' },
      { label: 'Help me understand WII', prompt: 'Explain the Workforce Intelligence Index (WII) framework in detail. What are its components and how is it measured?' },
      { label: 'What reports are published?', prompt: 'What types of research reports and publications does the Institute produce? How often are they released?' },
      { label: 'Explain the research mission', prompt: 'What is the research mission of the Zodiac Workforce Intelligence Institute? How does research translate into practical applications?' }
    ]
  },
  
  // JusWork Platform
  '/juswork': {
    domain: 'workforce_intelligence',
    title: 'Workforce Intelligence Assistant',
    welcome: "Hello! I'm your Workforce Intelligence Assistant. I can help with workforce readiness, diagnostics, and organizational insights.",
    placeholder: 'Ask about workforce intelligence or diagnostics...',
    quickActions: [
      { label: 'What is workforce intelligence?', prompt: 'Define workforce intelligence and explain why it matters for organizations in the AI era.' },
      { label: 'Explain WII assessment', prompt: 'How does the Workforce Intelligence Index assessment work? What dimensions does it measure and how can organizations use the results?' },
      { label: 'Explain readiness scoring', prompt: 'How is workforce readiness scored? What factors contribute to organizational readiness for AI integration?' },
      { label: 'Show workforce diagnostics', prompt: 'What workforce diagnostics tools and methodologies does JusWork provide? How can they help my organization?' },
      { label: 'What can JusWork do?', prompt: 'Give me an overview of the JusWork platform capabilities. What can organizations accomplish using JusWork?' }
    ]
  },
  
  // Observatory
  '/juswork/observatory': {
    domain: 'workforce_intelligence',
    title: 'Workforce Intelligence Assistant',
    welcome: "Welcome to the Observatory! I'm your Workforce Intelligence Assistant. I can help interpret WII scores, benchmarks, and workforce diagnostics.",
    placeholder: 'Ask about WII, benchmarks, or workforce analytics...',
    quickActions: [
      { label: 'Explain WII scoring', prompt: 'How is the Workforce Intelligence Index scored? What do the five dimensions (Trust, Alignment, Readiness, AI Capability, Resilience) measure?' },
      { label: 'Interpret my results', prompt: 'How should I interpret my WII assessment results? What do different score ranges indicate about workforce readiness?' },
      { label: 'Show industry benchmarks', prompt: 'What are the industry benchmarks for workforce intelligence? How do different sectors compare?' },
      { label: 'Improve my score', prompt: 'Based on WII methodology, what strategies can help improve workforce intelligence scores in each dimension?' },
      { label: 'What is the Observatory?', prompt: 'What is the Workforce Intelligence Observatory and how does it help organizations track and improve workforce capability?' }
    ]
  },
  
  // ProLab / Education
  '/juswork/prolab': {
    domain: 'education',
    title: 'Education Assistant',
    welcome: "Welcome to ProLab! I'm your Education Assistant. I can help explain lab concepts, missions, and guide your learning journey.",
    placeholder: 'Ask about labs, missions, or learning paths...',
    quickActions: [
      { label: 'Explain this lab', prompt: 'Tell me about this ProLab and what I will learn. What are the key concepts and skills covered?' },
      { label: 'Help me study', prompt: 'Help me understand the key concepts in this learning module. Can you summarize the main takeaways?' },
      { label: 'Give me an example', prompt: 'Can you give me a practical, real-world example of how this concept applies in the workplace?' },
      { label: 'Quiz me', prompt: 'Quiz me on the key concepts from this learning area. Ask me questions to test my understanding.' },
      { label: 'Break this down', prompt: 'Break down this topic step by step in simple terms. I want to understand the fundamentals.' }
    ]
  },
  
  // About Page
  '/about': {
    domain: 'admissions',
    title: 'Zodiac Assistant',
    welcome: "Hello! I'm here to tell you about the Zodiac Workforce Intelligence Institute. Ask me anything about our mission, programs, or services.",
    placeholder: 'Ask about the Institute...',
    quickActions: [
      { label: "What is Zodiac's mission?", prompt: 'What is the mission of the Zodiac Workforce Intelligence Institute? What does the organization aim to achieve?' },
      { label: 'Tell me about the institute', prompt: 'Give me an overview of the Zodiac Workforce Intelligence Institute. What programs and services do you offer?' },
      { label: 'What makes this unique?', prompt: 'What makes the Zodiac Workforce Intelligence Institute unique in the education and workforce development space?' },
      { label: 'Workforce intelligence vision', prompt: 'Explain the workforce intelligence vision. How does Zodiac see the future of workforce capability in the AI era?' }
    ]
  },
  
  // Contact Page
  '/contact': {
    domain: 'admissions',
    title: 'Zodiac Assistant',
    welcome: "Hello! Before you reach out, I might be able to help. What would you like to know?",
    placeholder: 'Ask a question before contacting us...',
    quickActions: [
      { label: 'How can I contact you?', prompt: 'What are the best ways to contact the Zodiac Workforce Intelligence Institute? What are your response times?' },
      { label: 'Learn more about programs', prompt: 'I want to learn more about your programs before I reach out. Can you give me an overview?' },
      { label: 'Partnership information', prompt: 'I am interested in partnership or collaboration opportunities. What options are available for organizations?' },
      { label: 'I have a general question', prompt: 'I have a general inquiry about the institute. Who should I contact and what information should I provide?' }
    ]
  }
};

// Get context based on current path
const getPageContext = (pathname) => {
  // Check for exact matches first
  if (ASSISTANT_CONTEXT_CONFIG[pathname]) {
    return ASSISTANT_CONTEXT_CONFIG[pathname];
  }
  
  // Check for prefix matches (more specific first)
  const sortedKeys = Object.keys(ASSISTANT_CONTEXT_CONFIG).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (pathname.startsWith(key) && key !== '/') {
      return ASSISTANT_CONTEXT_CONFIG[key];
    }
  }
  
  // Default fallback
  return ASSISTANT_CONTEXT_CONFIG['/'];
};

// Generate unique session ID
const generateSessionId = () => {
  const stored = sessionStorage.getItem('zodiac_assistant_session');
  if (stored) return stored;
  
  const newId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem('zodiac_assistant_session', newId);
  return newId;
};

// ============================================
// QUICK ACTION CHIPS COMPONENT
// ============================================

function QuickActionChips({ actions, onSelect, disabled }) {
  if (!actions || actions.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 px-1 py-2" data-testid="quick-actions-container">
      {actions.map((action, index) => (
        <button
          key={index}
          onClick={() => onSelect(action)}
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
            "bg-primary/5 text-primary border border-primary/20",
            "hover:bg-primary/10 hover:border-primary/30",
            "transition-colors duration-150",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "focus:outline-none focus:ring-2 focus:ring-primary/30"
          )}
          data-testid={`quick-action-${index}`}
        >
          <Sparkles className="w-3 h-3" />
          {action.label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// MAIN WIDGET COMPONENT
// ============================================

export function IntelligenceAssistantWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [error, setError] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const pageContext = getPageContext(location.pathname);
  
  // Extend pageContext with route for analytics
  const pageContextWithRoute = {
    ...pageContext,
    route: location.pathname
  };
  
  // Initialize analytics with session context
  useEffect(() => {
    assistantAnalytics.init(sessionId, user?.id || null, isAuthenticated);
  }, [sessionId, user, isAuthenticated]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  
  // Add welcome message when opening for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: pageContext.welcome,
        timestamp: new Date().toISOString(),
        showQuickActions: true
      }]);
      setHasInteracted(false);
    }
  }, [isOpen, messages.length, pageContext.welcome]);
  
  // Update welcome message when page changes (if no conversation started)
  useEffect(() => {
    if (isOpen && messages.length === 1 && messages[0].id === 'welcome') {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: pageContext.welcome,
        timestamp: new Date().toISOString(),
        showQuickActions: true
      }]);
      setHasInteracted(false);
    }
  }, [location.pathname, isOpen, messages, pageContext.welcome]);
  
  // Send message to Intelligence Core
  const sendMessageToCore = useCallback(async (messageText, isQuickAction = false, quickActionLabel = null) => {
    if (!messageText.trim() || isLoading) return;
    
    // Track message submission
    assistantAnalytics.trackMessageSubmitted(pageContextWithRoute, messageText, isQuickAction, quickActionLabel);
    
    const userMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: isQuickAction ? quickActionLabel : messageText.trim(),
      timestamp: new Date().toISOString(),
      isQuickAction
    };
    
    setMessages(prev => {
      // Remove showQuickActions flag from welcome message once user interacts
      const updated = prev.map(msg => 
        msg.id === 'welcome' ? { ...msg, showQuickActions: false } : msg
      );
      return [...updated, userMessage];
    });
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setHasInteracted(true);
    
    try {
      const response = await fetch(`${API_URL}/api/intelligence-core/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: isAuthenticated ? user?.id : null,
          session_id: sessionId,
          request_text: messageText.trim(),
          source_page: location.pathname,
          preferred_domain: pageContext.domain,
          metadata: {
            page_title: pageContext.title,
            authenticated: isAuthenticated,
            is_quick_action: isQuickAction,
            quick_action_label: quickActionLabel
          }
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to get response');
      }
      
      // Track successful response
      assistantAnalytics.trackResponseReceived(pageContextWithRoute, data);
      
      const assistantMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: data.response_text,
        timestamp: data.timestamp,
        agent: data.primary_agent,
        latency: data.latency_ms
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
    } catch (err) {
      console.error('Assistant error:', err);
      
      // Track error
      assistantAnalytics.trackError(pageContextWithRoute, err.message, 'message_submission');
      
      setError('Sorry, the Intelligence Assistant is temporarily unavailable. Please try again.');
      
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: 'I apologize, but I encountered an issue processing your request. Please try again in a moment.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, sessionId, location.pathname, pageContext.domain, pageContext.title, isAuthenticated, user, pageContextWithRoute]);
  
  // Handle manual message send
  const sendMessage = useCallback(() => {
    sendMessageToCore(inputValue, false, null);
  }, [inputValue, sendMessageToCore]);
  
  // Handle quick action selection
  const handleQuickAction = useCallback((action) => {
    // Track quick action click
    assistantAnalytics.trackQuickActionClicked(pageContextWithRoute, action);
    sendMessageToCore(action.prompt, true, action.label);
  }, [sendMessageToCore, pageContextWithRoute]);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const clearConversation = async () => {
    // Track conversation cleared
    assistantAnalytics.trackConversationCleared(pageContextWithRoute);
    
    try {
      await fetch(`${API_URL}/api/intelligence-core/clear-session/${sessionId}`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Failed to clear session:', err);
    }
    
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: pageContext.welcome,
      timestamp: new Date().toISOString(),
      showQuickActions: true
    }]);
    setError(null);
    setHasInteracted(false);
  };
  
  // Handle widget open with analytics
  const handleWidgetOpen = useCallback(() => {
    setIsOpen(true);
    assistantAnalytics.trackWidgetOpened(pageContextWithRoute);
  }, [pageContextWithRoute]);
  
  // Handle widget close with analytics
  const handleWidgetClose = useCallback(() => {
    assistantAnalytics.trackWidgetClosed(pageContextWithRoute, messages.length);
    setIsOpen(false);
  }, [pageContextWithRoute, messages.length]);
  
  // Handle widget minimize with analytics
  const handleWidgetMinimize = useCallback(() => {
    assistantAnalytics.trackWidgetMinimized(pageContextWithRoute, messages.length);
    setIsOpen(false);
  }, [pageContextWithRoute, messages.length]);
  
  // Don't show on auth pages
  if (location.pathname.startsWith('/auth')) {
    return null;
  }
  
  // Check if we should show quick actions
  const showQuickActions = !hasInteracted && messages.length <= 1;
  
  return (
    <>
      {/* Floating Launcher Button */}
      <button
        onClick={handleWidgetOpen}
        className={cn(
          "fixed bottom-6 right-6 z-50 flex items-center justify-center",
          "w-14 h-14 rounded-full shadow-lg",
          "bg-primary text-primary-foreground",
          "hover:bg-primary/90 hover:scale-105",
          "transition-all duration-200 ease-in-out",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
          isOpen && "scale-0 opacity-0 pointer-events-none"
        )}
        aria-label="Open Intelligence Assistant"
        data-testid="assistant-launcher-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
      
      {/* Chat Panel */}
      <div
        className={cn(
          "fixed bottom-6 right-6 z-50",
          "w-[380px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)]",
          "bg-card rounded-xl shadow-2xl border border-border",
          "flex flex-col overflow-hidden",
          "transition-all duration-300 ease-in-out",
          isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"
        )}
        data-testid="assistant-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">{pageContext.title}</h3>
              <p className="text-xs opacity-80">Powered by Zodiac Intelligence Core</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={clearConversation}
              className="p-2 rounded-md hover:bg-white/10 transition-colors"
              title="Clear conversation"
              data-testid="assistant-clear-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleWidgetMinimize}
              className="p-2 rounded-md hover:bg-white/10 transition-colors"
              title="Minimize"
              data-testid="assistant-minimize-btn"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleWidgetClose}
              className="p-2 rounded-md hover:bg-white/10 transition-colors"
              title="Close"
              data-testid="assistant-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
          {messages.map((msg) => (
            <div key={msg.id}>
              <div
                className={cn(
                  "flex gap-3",
                  msg.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                )}
                
                <div
                  className={cn(
                    "max-w-[80%] rounded-lg px-4 py-3 text-sm",
                    msg.role === 'user' 
                      ? "bg-primary text-primary-foreground" 
                      : msg.isError 
                        ? "bg-destructive/10 text-destructive border border-destructive/20"
                        : "bg-card border border-border text-foreground"
                  )}
                >
                  <div className="whitespace-pre-wrap break-words leading-relaxed">
                    {msg.content}
                  </div>
                  {msg.agent && !msg.isError && (
                    <div className="mt-2 pt-2 border-t border-border/50 text-xs opacity-60 flex items-center gap-2">
                      <span className="capitalize">{msg.agent.replace('_', ' ')} Agent</span>
                      {msg.latency && <span>• {(msg.latency / 1000).toFixed(1)}s</span>}
                    </div>
                  )}
                  {msg.isQuickAction && (
                    <div className="mt-1 flex items-center gap-1 text-xs opacity-70">
                      <Sparkles className="w-3 h-3" />
                      <span>Quick action</span>
                    </div>
                  )}
                </div>
                
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-secondary-foreground" />
                  </div>
                )}
              </div>
              
              {/* Quick Actions after welcome message */}
              {msg.showQuickActions && showQuickActions && (
                <div className="mt-3 ml-11">
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Suggested questions:
                  </p>
                  <QuickActionChips 
                    actions={pageContext.quickActions} 
                    onSelect={handleQuickAction}
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-primary" />
              </div>
              <div className="bg-card border border-border rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Error Banner */}
        {error && (
          <div className="px-4 py-2 bg-destructive/10 text-destructive text-xs border-t border-destructive/20">
            {error}
          </div>
        )}
        
        {/* Input Area */}
        <div className="p-4 border-t border-border bg-card">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={pageContext.placeholder}
              disabled={isLoading}
              className={cn(
                "flex-1 resize-none rounded-lg px-3 py-2 text-sm",
                "bg-muted border border-input",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "placeholder:text-muted-foreground"
              )}
              rows={1}
              style={{ minHeight: '40px', maxHeight: '100px' }}
              data-testid="assistant-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
              className="flex-shrink-0 h-10 w-10"
              data-testid="assistant-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground text-center">
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      </div>
    </>
  );
}

export default IntelligenceAssistantWidget;
